import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  Space,
  message,
  Typography,
  Row,
  Col,
  Checkbox,
  Table,
  InputNumber,
} from 'antd';
import { CheckOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { Alert } from 'flowbite-react';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { quotationService, CreateQuotationData, Quotation } from '../services/quotations';
import { customerService } from '../services/customers';
import { invoiceService } from '../services/invoices';
import { staffService, Staff } from '../services/staffs';
import { businessService } from '../services/businesses';
import { taskService, Task } from '../services/tasks';
import dayjs, { Dayjs } from 'dayjs';

const { Title, Text } = Typography;

type LayoutHeaderContext = {
  setHeaderContent: (content: React.ReactNode) => void;
};

export const QuotationFormPage: React.FC = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const { setHeaderContent } = useOutletContext<LayoutHeaderContext>();
  const { user } = useAuth();
  const canCreateQuotation = hasPermission(user, 'billing.quotations.create');
  const canUpdate = hasPermission(user, 'billing.quotations.update');
  const canDelete = hasPermission(user, 'billing.quotations.delete');
  const canApprove = hasPermission(user, 'billing.quotations.verify');
  const canViewCustomerInfo = hasPermission(user, 'billing.quotations.customer-information.view');
  const canCreateCustomerInfo = hasPermission(user, 'billing.quotations.customer-information.create');
  const canUpdateCustomerInfo = hasPermission(user, 'billing.quotations.customer-information.update');
  const canViewQuotationDetails = hasPermission(user, 'billing.quotations.quotation-details.view');
  const canCreateQuotationDetails = hasPermission(user, 'billing.quotations.quotation-details.create');
  const canUpdateQuotationDetails = hasPermission(user, 'billing.quotations.quotation-details.update');
  const canViewTaskInfo = hasPermission(user, 'billing.quotations.task-information.view');
  const canCreateTaskInfo = hasPermission(user, 'billing.quotations.task-information.create');
  const canUpdateTaskInfo = hasPermission(user, 'billing.quotations.task-information.update');
  const isCreateMode = !isEdit;
  const isSenior = Boolean(user?.roles?.includes('senior'));
  const isSeniorOnlyStaffEdit = isEdit && isSenior;
  const canEditAny = canUpdate || canUpdateCustomerInfo || canUpdateQuotationDetails || canUpdateTaskInfo;
  const canCreateAny = canCreateQuotation || canCreateCustomerInfo || canCreateQuotationDetails || canCreateTaskInfo;
  const allowCustomerInfo = isCreateMode ? canCreateCustomerInfo : canViewCustomerInfo;
  const allowQuotationDetails = isCreateMode ? canCreateQuotationDetails : canViewQuotationDetails;
  const allowTaskInfo = isCreateMode ? canCreateTaskInfo : canViewTaskInfo;
  const isCustomerInfoEditing = (isCreateMode ? canCreateCustomerInfo : canUpdateCustomerInfo) && !isSeniorOnlyStaffEdit;
  const isQuotationDetailsEditing = (isCreateMode ? canCreateQuotationDetails : canUpdateQuotationDetails) && !isSeniorOnlyStaffEdit;
  const isTaskInfoEditing = (isCreateMode ? canCreateTaskInfo : canUpdateTaskInfo) && !isSeniorOnlyStaffEdit;
  const hasAnySection = allowCustomerInfo || allowQuotationDetails;
  const hasBothTopSections = allowCustomerInfo && allowQuotationDetails;
  const [submitting, setSubmitting] = useState(false);
  const [loadingQuotation, setLoadingQuotation] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [resending, setResending] = useState(false);
  const [submitNotice, setSubmitNotice] = useState<{ type: 'success' | 'failure'; message: string } | null>(null);
  const [approving, setApproving] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  useEffect(() => {
    setHeaderContent(
      <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
        Quotations
      </span>
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [quotationItems, setQuotationItems] = useState<any[]>([]);
  const [customerType, setCustomerType] = useState<'personal' | 'business'>('personal');
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [selectedBusiness, setSelectedBusiness] = useState<any>(null);
  const [quotationName, setQuotationName] = useState<string>('');
  const [assignedStaffId, setAssignedStaffId] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(isCreateMode ? canCreateAny : false); // For new quotations, default to editing when allowed
  const [currentStatus, setCurrentStatus] = useState<'Plan' | 'Sent' | 'Approve'>('Plan');
  const navigate = useNavigate();
  const normalizeStatus = (status?: string): 'Plan' | 'Sent' | 'Approve' => {
    if (!status) return 'Plan';
    const normalized = status.toLowerCase();
    if (normalized === 'draft' || normalized === 'plan') return 'Plan';
    if (normalized === 'pending' || normalized === 'sent') return 'Sent';
    if (normalized === 'approved' || normalized === 'approve') return 'Approve';
    return 'Plan';
  };

  useEffect(() => {
    fetchInitialData();
    if (isEdit && id) {
      loadQuotation(parseInt(id, 10));
    }
  }, [isEdit, id]);

  const fetchInitialData = async () => {
    try {
      const [customersResponse, businessesResponse, invoicesResponse, staffResponse, tasksResponse] = await Promise.all([
        customerService.getAll(),
        businessService.getAll().catch(() => ({ data: [] })),
        invoiceService.getAll(),
        staffService.getAll().catch(() => []),
        taskService.getAll().catch(() => ({ data: [] })),
      ]);
      
      // Ensure customers are loaded with agent and team relationships
      const customersData = customersResponse.data || [];
      setCustomers(customersData);
      setBusinesses(businessesResponse.data || []);
      setInvoices(invoicesResponse.data || []);
      setStaff(Array.isArray(staffResponse) ? staffResponse : []);
      setTasks(tasksResponse.data || []);
    } catch (error) {
      console.error('Failed to load initial data:', error);
      message.error('Failed to load initial data');
    }
  };

  const loadQuotation = async (quotationId: number) => {
    setLoadingQuotation(true);
    try {
      const quotation = await quotationService.getById(quotationId);
      setAssignedStaffId(quotation.staff_id ?? quotation.staff?.id ?? null);
      console.log('🔍 [QuotationForm] Loaded quotation data:', quotation);
      console.log('🔍 [QuotationForm] Quotation status from API:', quotation.status);
      console.log('🔍 [QuotationForm] Quotation status type:', typeof quotation.status);
      const status = normalizeStatus(quotation.status);
      const quotationType = quotation.type || 'personal';
      const referenceId = quotation.reference_id ?? undefined;
      form.setFieldsValue({
        ...quotation,
        customer_id: quotationType === 'personal' ? referenceId ?? quotation.customer_id : quotation.customer_id,
        business_id: quotationType === 'business' ? referenceId : undefined,
        date: dayjs(quotation.date),
        is_sent: quotation.is_sent ?? (status === 'Sent' || status === 'Approve'),
        status: status,
      });
      setCustomerType(quotationType);
      setCurrentStatus(status as 'Plan' | 'Sent' | 'Approve');
      console.log('🔍 [QuotationForm] Form status after setFieldsValue:', form.getFieldValue('status'));
      // Load quotation items
      setQuotationItems(quotation.items || []);
      // Set quotation name immediately after loading
      setQuotationName(quotation.quotation_number || '');
      // Load tasks - Commented out
      // setTasks(quotation.tasks || []);
      if (quotationType === 'business' && referenceId) {
        try {
          const business = await businessService.getById(referenceId);
          setSelectedBusiness(business);
          form.setFieldsValue({ business_id: business.id });
          if (business.customer_id) {
            const customer = await customerService.getById(business.customer_id);
            setSelectedCustomer(customer);
            form.setFieldsValue({ customer_id: business.customer_id });
            const customerExists = customers.find(c => c.id === business.customer_id);
            if (!customerExists) {
              setCustomers(prev => [...prev, customer]);
            } else {
              setCustomers(prev => prev.map(c => c.id === business.customer_id ? customer : c));
            }
          }
        } catch {
          message.error('Failed to load business details');
        }
      } else if (referenceId || quotation.customer_id) {
        const customerId = referenceId ?? quotation.customer_id;
        if (customerId) {
          try {
            const customer = await customerService.getById(customerId);
            setSelectedCustomer(customer);
            const customerExists = customers.find(c => c.id === customerId);
            if (!customerExists) {
              setCustomers(prev => [...prev, customer]);
            } else {
              setCustomers(prev => prev.map(c => c.id === customerId ? customer : c));
            }
          } catch {
            const business = businesses.find(b => b.customer_id === customerId);
            if (business) {
              setSelectedBusiness(business);
              setCustomerType('business');
              form.setFieldsValue({ business_id: business.id });
            }
          }
        }
      }
    } catch {
      message.error('Failed to load quotation');
      navigate('/quotations');
    } finally {
      setLoadingQuotation(false);
    }
  };

  const handleCustomerTypeChange = (type: 'personal' | 'business') => {
    setCustomerType(type);
    setSelectedCustomer(null);
    setSelectedBusiness(null);
    form.setFieldsValue({
      customer_id: undefined,
      business_id: undefined,
      agent_id: undefined,
      team_id: undefined,
    });
  };

  const handleCustomerChange = async (customerId: number) => {
    if (customerId) {
      try {
        setCustomerType('personal');
        const customer = await customerService.getById(customerId);
        setSelectedCustomer(customer);
        form.setFieldsValue({
          agent_id: customer.agent_id || undefined,
          team_id: customer.team_id || undefined,
        });
        // Update customers list to include the full customer data with agent and team
        setCustomers(prev => {
          const existing = prev.find(c => c.id === customerId);
          if (existing) {
            return prev.map(c => c.id === customerId ? customer : c);
          }
          return [...prev, customer];
        });
      } catch {
        message.error('Failed to load customer details');
      }
    } else {
      setSelectedCustomer(null);
      form.setFieldsValue({
        agent_id: undefined,
        team_id: undefined,
      });
    }
  };

  const handleBusinessChange = async (businessId: number) => {
    if (businessId) {
      try {
        setCustomerType('business');
        const business = await businessService.getById(businessId);
        setSelectedBusiness(business);
        if (business.customer_id) {
          const customer = await customerService.getById(business.customer_id);
          setSelectedCustomer(customer);
          form.setFieldsValue({
            customer_id: business.customer_id,
            agent_id: business.agent_id || customer.agent_id || undefined,
            team_id: customer.team_id || undefined,
          });
        } else {
          form.setFieldsValue({
            agent_id: business.agent_id || undefined,
            team_id: undefined,
          });
        }
      } catch {
        message.error('Failed to load business details');
      }
    } else {
      setSelectedBusiness(null);
      form.setFieldsValue({
        customer_id: undefined,
        agent_id: undefined,
        team_id: undefined,
      });
    }
  };

  const handleStaffChange = async (staffId?: number) => {
    if (!staffId) {
      form.setFieldsValue({ team_id: undefined });
      return;
    }

    const selected = staff.find((member) => member.id === staffId);
    if (selected?.team_id) {
      form.setFieldsValue({ team_id: selected.team_id });
      return;
    }

    try {
      const staffMember = await staffService.getById(staffId);
      if (staffMember.team_id) {
        form.setFieldsValue({ team_id: staffMember.team_id });
      }
    } catch {
      message.error('Failed to load staff details');
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this quotation?')) return;
    
    try {
      await quotationService.delete(parseInt(id, 10));
      message.success('Quotation deleted');
      navigate('/quotations');
    } catch {
      message.error('Failed to delete quotation');
    }
  };

  const handleGeneratePdf = async () => {
    if (!id) return;
    setGeneratingPdf(true);
    try {
      const pdfBlob = await quotationService.getPdf(parseInt(id, 10));
      const fileName = `${form.getFieldValue('quotation_number') || 'quotation'}.update`;
      const url = window.URL.createObjectURL(new Blob([pdfBlob], { type: 'application/pdf' }));
      const newWindow = window.open(url);
      if (!newWindow) {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
      }
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 10000);
    } catch {
      message.error('Failed to generate quotation PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleApprove = async () => {
    if (!id) return;
    setApproving(true);
    try {
      await quotationService.approve(parseInt(id, 10));
      setSubmitNotice({ type: 'success', message: 'Quotation approved successfully.' });
      await loadQuotation(parseInt(id, 10));
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message || error?.message || 'Failed to approve quotation.';
      setSubmitNotice({ type: 'failure', message: apiMessage });
    } finally {
      setApproving(false);
    }
  };

  const handleResend = async () => {
    if (!id) return;
    setResending(true);
    try {
      await quotationService.resend(parseInt(id, 10));
      message.success('Quotation resent');
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 4000);
      await loadQuotation(parseInt(id, 10));
    } catch {
      message.error('Failed to resend quotation');
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      // Determine status based on is_sent
      let finalStatus = normalizeStatus(values.status);
      if (values.is_sent === true) {
        if (finalStatus !== 'Approve') {
          finalStatus = 'Sent';
        }
      } else if (values.is_sent === false) {
        finalStatus = 'Plan';
      }

      let businessCustomerId = values.customer_id;
      if (values.business_id) {
        const matchingBusiness = selectedBusiness?.id === values.business_id
          ? selectedBusiness
          : businesses.find(b => b.id === values.business_id);
        businessCustomerId = matchingBusiness?.customer_id || businessCustomerId;

        if (!businessCustomerId) {
          const business = await businessService.getById(values.business_id);
          setSelectedBusiness(business);
          businessCustomerId = business.customer_id || businessCustomerId;
        }
      }

      const payload: CreateQuotationData = {
        ...values,
        date: values.date.format('YYYY-MM-DD'),
        task_id: values.task_id || undefined,
        task_no: values.task_no || undefined,
        // If business_id is selected, use business's customer_id
        customer_id: values.business_id ? businessCustomerId : values.customer_id,
        type: customerType,
        reference_id: values.business_id ? values.business_id : values.customer_id,
        quotation_number: values.quotation_number || undefined,
        is_sent: values.is_sent !== undefined ? values.is_sent : false,
        status: finalStatus, // Use the determined status
          items: quotationItems.map((item, index) => ({
            id: typeof item.id === 'number' ? item.id : undefined,
            description: item.description || '',
            unit: item.unit,
            price_per_unit: item.price_per_unit || 0,
            discount: item.discount || 0,
            sst_rate: item.sst_rate !== undefined && item.sst_rate !== null ? Number(item.sst_rate) : 8,
            sst: (() => {
              const unit = typeof item.unit === 'number' ? item.unit : parseFloat(item.unit || '0');
              const price = typeof item.price_per_unit === 'number' ? item.price_per_unit : parseFloat(item.price_per_unit || '0');
              const discount = typeof item.discount === 'number' ? item.discount : parseFloat(item.discount || '0');
              const base = (unit || 0) * (price || 0) - (discount || 0);
              const rate = item.sst_rate !== undefined && item.sst_rate !== null ? Number(item.sst_rate) : 8;
              return item.sst !== undefined && item.sst !== null ? item.sst : base * (rate / 100);
            })(),
          amount: (() => {
            const unit = typeof item.unit === 'number' ? item.unit : parseFloat(item.unit || '0');
            const price = typeof item.price_per_unit === 'number' ? item.price_per_unit : parseFloat(item.price_per_unit || '0');
            const discount = typeof item.discount === 'number' ? item.discount : parseFloat(item.discount || '0');
            const base = (unit || 0) * (price || 0) - (discount || 0);
            const rate = item.sst_rate !== undefined && item.sst_rate !== null ? Number(item.sst_rate) : 8;
            const sstValue = item.sst !== undefined && item.sst !== null ? item.sst : base * (rate / 100);
            return item.amount !== undefined && item.amount !== null ? item.amount : base + sstValue;
          })(),
          sort_order: index,
        })),
      };
      // Remove business_id from payload as backend doesn't expect it
      delete (payload as any).business_id;

      if (isEdit && id) {
        await quotationService.update(parseInt(id, 10), payload);
        setSubmitNotice({ type: 'success', message: 'Quotation updated successfully.' });
      } else {
        const result = await quotationService.create(payload);
        // Update form with generated quotation number
        if (result.quotation_number) {
          form.setFieldsValue({ quotation_number: result.quotation_number });
        }
        message.success('Quotation created successfully');
      }
      
      if (isEdit && id) {
        navigate('/quotations', { state: { notice: { type: 'success', message: 'Quotation updated successfully.' } } });
      } else {
        navigate('/quotations', { state: { notice: { type: 'success', message: 'Quotation created successfully.' } } });
      }
    } catch {
      setSubmitNotice({
        type: 'failure',
        message: isEdit ? 'Failed to update quotation.' : 'Failed to create quotation.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const loadNextNumber = async (date?: string) => {
    try {
      console.log('Loading next quotation number for date:', date);
      const nextNumber = await quotationService.getNextNumber(date);
      console.log('Received next number:', nextNumber);
      if (nextNumber) {
        form.setFieldsValue({ quotation_number: nextNumber });
      } else {
        console.warn('No next number received, using fallback');
        // If API fails, use a fallback preview
        const prefix = 'QT';
        const year = new Date().getFullYear().toString().slice(-2);
        const month = (new Date().getMonth() + 1).toString().padStart(2, '0');
        form.setFieldsValue({ quotation_number: `${prefix}${year}${month}-001` });
      }
    } catch (error) {
      console.error('Failed to load next quotation number:', error);
      // If API fails, use a fallback preview
      const prefix = 'QT';
      const year = new Date().getFullYear().toString().slice(-2);
      const month = (new Date().getMonth() + 1).toString().padStart(2, '0');
      form.setFieldsValue({ quotation_number: `${prefix}${year}${month}-001` });
    }
  };

  useEffect(() => {
    if (!isEdit) {
      const initialDate = dayjs();
      form.setFieldsValue({
        date: initialDate,
      });
      // Load next number from backend
      loadNextNumber(initialDate.format('YYYY-MM-DD'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  // Update number when date changes
  const handleDateChange = (date: any) => {
    if (!isEdit && date) {
      loadNextNumber(date.format('YYYY-MM-DD'));
    }
  };


  const getQuotationDisplayName = () => {
    if (isEdit && id) {
      return form.getFieldValue('quotation_number') || '';
    }
    return 'New Quotation';
  };

  useEffect(() => {
    if (isEdit && id && (customers.length > 0 || selectedCustomer)) {
      const displayName = getQuotationDisplayName();
      setQuotationName(displayName);
    }
  }, [isEdit, id, customers, selectedCustomer, form.getFieldValue('quotation_number'), form.getFieldValue('customer_id')]);

  const statuses: Array<'Plan' | 'Sent' | 'Approve'> = ['Plan', 'Sent', 'Approve'];
  const statusConfig = {
    Plan: { bg: '#E6F7FF', border: '#40A9FF', color: '#1890ff', dot: '#1890ff', label: 'Plan' },
    Sent: { bg: '#FFF7E6', border: '#FFA940', color: '#FF8C00', dot: '#FF8C00', label: 'Sent' },
    Approve: { bg: '#F6FFED', border: '#73D13D', color: '#52C41A', dot: '#52C41A', label: 'Approve' },
  };
  const isFullAccess = Boolean(user?.roles?.some((role) => ['owner', 'admin', 'developer'].includes(role)));
  const isManager = Boolean(user?.roles?.includes('manager'));
  const canEditRecord = Boolean(canEditAny);

  return (
    <div>
      {/* Header */}
        <div style={{
          marginBottom: 24,
          paddingBottom: 16,
          borderBottom: '1px solid #f0f0f0',
        }}>
          {/* First row: Title and buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
            marginBottom: 12,
            paddingBottom: 12,
          borderBottom: '1px solid #f0f0f0',
        }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff', fontSize: 28, fontWeight: 600 }}>
            {isEdit ? (quotationName || 'Loading...') : 'New Quotation'}
          </Title>
          <Space>
              {isEdit && (
                <Button onClick={handleGeneratePdf} loading={generatingPdf}>
                  Generate PDF
                </Button>
              )}
              {isEdit && !isEditing && canEditAny && canEditRecord && (
                <Button onClick={() => setIsEditing(true)}>
                  Edit
                </Button>
              )}
              {isEdit && canApprove && currentStatus === 'Sent' && (
                <Button loading={approving} onClick={handleApprove}>
                  Approve
                </Button>
              )}
              {isEdit && isEditing && canDelete && (
                <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                  Delete
                </Button>
              )}
            </Space>
          </div>
          {/* Second row: Status badges, aligned to the right */}
          {isEdit && (
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}>
                  <Space size={8}>
                    {statuses.map((status) => {
                      const isActive = currentStatus === status;
                      const config = statusConfig[status];
                      return (
                        <div
                          key={status}
                          style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            backgroundColor: isActive ? config.bg : '#f5f5f5',
                            border: isActive ? `1px solid ${config.border}` : '1px solid #d9d9d9',
                            color: isActive ? config.color : '#595959',
                            fontWeight: isActive ? 500 : 400,
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            transition: 'all 0.2s',
                          }}
                        >
                          <span style={{
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            backgroundColor: isActive ? config.dot : '#8c8c8c',
                          }}></span>
                          {config.label}
                        </div>
                      );
                    })}
                  </Space>
        </div>
      )}
        </div>
      {submitNotice && (
        <div style={{ marginBottom: 16 }}>
          <Alert color={submitNotice.type} onDismiss={() => setSubmitNotice(null)}>
            {submitNotice.message}
          </Alert>
        </div>
      )}
      {resendSuccess && (
        <div style={{ marginBottom: 16 }}>
          <Alert color="success" onDismiss={() => setResendSuccess(false)}>
            Resend successful.
          </Alert>
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onFinishFailed={(errorInfo) => {
          const errorMessages = errorInfo.errorFields
            .flatMap((field) => field.errors)
            .filter(Boolean);
          setSubmitNotice({
            type: 'failure',
            message: errorMessages.length > 0 ? errorMessages.join(' ') : 'Please fix the form errors before submitting.',
          });
        }}
        disabled={loadingQuotation || !isEditing}
        initialValues={{
          status: 'Plan',
        }}
        onValuesChange={(changedValues) => {
          if (changedValues.customer_id !== undefined || changedValues.quotation_number !== undefined) {
            const displayName = getQuotationDisplayName();
            setQuotationName(displayName);
          }
          if (changedValues.status !== undefined) {
            setCurrentStatus(normalizeStatus(changedValues.status) as 'Plan' | 'Sent' | 'Approve');
          }
        }}
      >
        {/* Hidden field to register status in form */}
        <Form.Item name="status" hidden>
          <Input type="hidden" />
        </Form.Item>
        {hasAnySection && (
        <Row gutter={24}>
          {/* Left Column */}
          {allowCustomerInfo && (
            <Col span={hasBothTopSections ? 12 : 24}>
            <Card title="Customer Information" style={{ marginBottom: 16 }} bodyStyle={{ padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {/* Customer Type - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Type</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing && isCustomerInfoEditing ? (
                        <Space>
                          <Button
                            type={customerType === 'personal' ? 'primary' : 'default'}
                            onClick={() => handleCustomerTypeChange('personal')}
                            size="small"
                          >
                            Personal
                          </Button>
                          <Button
                            type={customerType === 'business' ? 'primary' : 'default'}
                            onClick={() => handleCustomerTypeChange('business')}
                            size="small"
                          >
                            Business
                          </Button>
                        </Space>
                      ) : (
                        <div>{customerType === 'personal' ? 'Personal' : 'Business'}</div>
                      )}
                    </td>
                  </tr>
                  {/* Customer/Business - 单独一行，合并后三列 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>{customerType === 'personal' ? 'Personal' : 'Business'}</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing && isCustomerInfoEditing ? (
                        customerType === 'personal' ? (
                          <Form.Item
                            name="customer_id"
                            rules={[{ required: true, message: 'Please select customer' }]}
                            style={{ margin: 0 }}
                          >
                            <Select
                              placeholder="Select Personal"
                              allowClear
                              showSearch
                              optionFilterProp="children"
                              onChange={handleCustomerChange}
                            >
                              {customers.map((customer) => (
                                <Select.Option key={customer.id} value={customer.id}>
                                  {customer.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        ) : (
                          <Form.Item
                            name="business_id"
                            rules={[{ required: true, message: 'Please select business' }]}
                            style={{ margin: 0 }}
                          >
                            <Select
                              placeholder="Select Business"
                              allowClear
                              showSearch
                              optionFilterProp="children"
                              onChange={handleBusinessChange}
                            >
                              {businesses.map((business) => (
                                <Select.Option key={business.id} value={business.id}>
                                  {business.name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        )
                      ) : (
                        <div>
                          {customerType === 'personal'
                            ? (selectedCustomer?.name || form.getFieldValue('customer_id') ? customers.find(c => c.id === form.getFieldValue('customer_id'))?.name || '-' : '-')
                            : (selectedBusiness?.name || form.getFieldValue('business_id') ? businesses.find(b => b.id === form.getFieldValue('business_id'))?.name || '-' : '-')
                          }
                        </div>
                      )}
                    </td>
                  </tr>
                  {/* Address - 单独一行 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Address</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      <Form.Item shouldUpdate={(prevValues, currentValues) => 
                            prevValues.customer_id !== currentValues.customer_id || 
                            prevValues.business_id !== currentValues.business_id
                          } noStyle>
                            {({ getFieldValue }) => {
                              const customerId = getFieldValue('customer_id');
                              const businessId = getFieldValue('business_id');
                              let address = '';
                              
                              if (customerId && selectedCustomer?.contacts && selectedCustomer.contacts.length > 0) {
                                const contact = selectedCustomer.contacts[0];
                                address = [contact.address1, contact.address2].filter(Boolean).join('\n');
                              } else if (businessId && selectedBusiness?.contacts && selectedBusiness.contacts.length > 0) {
                                const contact = selectedBusiness.contacts[0];
                                address = [contact.main_address1, contact.main_address2].filter(Boolean).join('\n');
                              }

                              return <div style={{ whiteSpace: 'pre-line' }}>{address || '-'}</div>;
                            }}
                          </Form.Item>
                    </td>
                  </tr>
                  {/* Email - 单独一行 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Email</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => 
                            prevValues.customer_id !== currentValues.customer_id || 
                            prevValues.business_id !== currentValues.business_id
                          } noStyle>
                            {({ getFieldValue }) => {
                              const customerId = getFieldValue('customer_id');
                              const businessId = getFieldValue('business_id');
                              let email = '';
                              
                              if (customerId && selectedCustomer) {
                                email = selectedCustomer.email || selectedCustomer.contacts?.[0]?.email || '';
                              } else if (businessId && selectedBusiness) {
                                const contactEmail = selectedBusiness.contacts?.[0]?.email || '';
                                email = (selectedBusiness as any).email || contactEmail;
                              }

                              return <div>{email || '-'}</div>;
                            }}
                          </Form.Item>
                    </td>
                  </tr>
                  {/* Contact - 单独一行 */}
                  <tr>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Contact</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => 
                            prevValues.customer_id !== currentValues.customer_id || 
                            prevValues.business_id !== currentValues.business_id
                          } noStyle>
                            {({ getFieldValue }) => {
                              const customerId = getFieldValue('customer_id');
                              const businessId = getFieldValue('business_id');
                              let contact = '';
                              
                              if (customerId && selectedCustomer?.contacts && selectedCustomer.contacts.length > 0) {
                                const contactData = selectedCustomer.contacts[0];
                                contact = contactData.contact_number || contactData.email || '';
                              } else if (businessId && selectedBusiness?.contacts && selectedBusiness.contacts.length > 0) {
                                const contactData = selectedBusiness.contacts[0];
                                contact = contactData.contact_number || contactData.email || '';
                              }

                              return <div>{contact || '-'}</div>;
                            }}
                          </Form.Item>
                    </td>
                  </tr>
                </tbody>
              </table>
            </Card>
            </Col>
          )}

          {/* Right Column */}
          {allowQuotationDetails && (
            <Col span={hasBothTopSections ? 12 : 24}>
            <Card title="Quotation Details" style={{ marginBottom: 16 }} bodyStyle={{ padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {/* Quotation No. - 单独一行 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Quotation No.</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing && isQuotationDetailsEditing ? (
                        <Form.Item name="quotation_number" style={{ margin: 0 }}>
                          <Input placeholder="Auto-generated" />
                        </Form.Item>
                      ) : (
                        <div>{form.getFieldValue('quotation_number') || '-'}</div>
                      )}
                    </td>
                  </tr>
                  {/* Invoice No. - 单独一行 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Invoice No.</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <Form.Item name="invoice_id" noStyle hidden>
                            <Input type="hidden" />
                          </Form.Item>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.invoice_id !== currentValues.invoice_id} noStyle>
                            {({ getFieldValue }) => {
                              const invoiceId = getFieldValue('invoice_id');
                              const invoice = invoices.find(i => i.id === invoiceId);

                              return <div>{invoice?.invoice_number || '-'}</div>;
                            }}
                          </Form.Item>
                    </td>
                  </tr>
                  {/* Date - 单独一行 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Date</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing && isQuotationDetailsEditing ? (
                        <Form.Item
                          name="date"
                          rules={[{ required: true, message: 'Please select date' }]}
                          style={{ margin: 0 }}
                        >
                          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" onChange={handleDateChange} />
                        </Form.Item>
                      ) : (
                        <div>
                          {form.getFieldValue('date') 
                            ? (dayjs.isDayjs(form.getFieldValue('date')) 
                              ? form.getFieldValue('date').format('DD/MM/YYYY')
                              : dayjs(form.getFieldValue('date')).format('DD/MM/YYYY'))
                            : '-'
                          }
                      </div>
                      )}
                    </td>
                  </tr>
                  {/* Advisor - 单独一行 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Advisor</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <Form.Item name="agent_id" noStyle hidden>
                            <Input type="hidden" />
                          </Form.Item>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => 
                            prevValues.customer_id !== currentValues.customer_id || 
                            prevValues.business_id !== currentValues.business_id
                          } noStyle>
                            {({ getFieldValue }) => {
                              const customerId = getFieldValue('customer_id');
                              const businessId = getFieldValue('business_id');
                              let advisorName = '';
                              
                              if (customerId && selectedCustomer) {
                                advisorName =
                                  selectedCustomer.advisorStaff?.name
                                  || selectedCustomer.advisor_staff?.name
                                  || selectedCustomer.agent?.name
                                  || '';
                              } else if (businessId && selectedBusiness) {
                                advisorName =
                                  selectedBusiness.advisorStaff?.name
                                  || selectedBusiness.advisor_staff?.name
                                  || selectedBusiness.agent?.name
                                  || '';
                              }

                              return <div>{advisorName || '-'}</div>;
                            }}
                          </Form.Item>
                    </td>
                  </tr>
                  {/* Team - 单独一行 */}
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Team</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                          <Form.Item name="team_id" noStyle hidden>
                            <Input type="hidden" />
                          </Form.Item>
                          <Form.Item shouldUpdate={(prevValues, currentValues) => 
                            prevValues.customer_id !== currentValues.customer_id || 
                            prevValues.business_id !== currentValues.business_id
                          } noStyle>
                            {({ getFieldValue }) => {
                              const customerId = getFieldValue('customer_id');
                              const businessId = getFieldValue('business_id');
                              const staffId = getFieldValue('staff_id');
                              let teamName = '';
                              
                              if (customerId && selectedCustomer?.team) {
                                teamName = selectedCustomer.team.name || '';
                              } else if (staffId) {
                                const staffMember = staff.find(member => member.id === staffId);
                                teamName = staffMember?.team?.name || '';
                              }

                              return <div>{teamName || '-'}</div>;
                            }}
                          </Form.Item>
                    </td>
                  </tr>
                  {/* Staff - 单独一行 */}
                  <tr>
                    <td style={{ padding: '12px', width: '120px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                      <strong>Staff</strong>
                    </td>
                    <td colSpan={3} style={{ padding: '12px', verticalAlign: 'top' }}>
                      {isEditing && (isQuotationDetailsEditing || isSeniorOnlyStaffEdit) ? (
                        <Form.Item name="staff_id" style={{ margin: 0 }}>
                          <Select
                            placeholder="Select Staff"
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            onChange={handleStaffChange}
                          >
                            {staff.map((user) => (
                              <Select.Option key={user.id} value={user.id}>
                                {user.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      ) : (
                        <div>
                          {form.getFieldValue('staff_id') 
                            ? (staff.find(s => s.id === form.getFieldValue('staff_id'))?.name || '-')
                            : '-'
                          }
                      </div>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </Card>
          </Col>
          )}
        </Row>
        )}

        {/* Bottom Section */}
        {allowTaskInfo && (
        <Card title="Task Information" style={{ marginBottom: 24 }} bodyStyle={{ padding: '16px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
            <tbody>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px', width: '180px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>Task No.</strong>
                </td>
                <td style={{ padding: '12px', verticalAlign: 'top' }}>
                  {isEditing && isTaskInfoEditing ? (
                    <Form.Item name="task_id" style={{ margin: 0 }}>
                      <Select
                        placeholder="Select Task"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        onChange={(value) => {
                          const selectedTask = tasks.find((task) => task.id === value);
                          form.setFieldsValue({ task_no: selectedTask?.reference_number || '' });
                        }}
                      >
                        {tasks.map((task) => {
                          const label = task.reference_number || `#${task.id.toString().padStart(5, '0')}`;
                          return (
                            <Select.Option key={task.id} value={task.id}>
                              {label}
                            </Select.Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  ) : (
                    <div>
                      {form.getFieldValue('task_id') ? (
                        <span
                          style={{ color: '#1890ff', cursor: 'pointer' }}
                          onClick={() => navigate(`/tasks/${form.getFieldValue('task_id')}/edit`)}
                        >
                          {form.getFieldValue('task_no') || '-'}
                        </span>
                      ) : (
                        <span>{form.getFieldValue('task_no') || '-'}</span>
                      )}
                    </div>
                  )}
                </td>
              </tr>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px', width: '180px', verticalAlign: 'top', backgroundColor: '#fafafa' }}>
                  <strong>Description</strong>
                </td>
                <td style={{ padding: '12px', verticalAlign: 'top' }}>
                  {isEditing && isTaskInfoEditing ? (
                    <Form.Item name="task_description" style={{ margin: 0 }}>
                      <Input placeholder="Enter Description" />
                    </Form.Item>
                  ) : (
                    <div>{form.getFieldValue('task_description') || '-'}</div>
                  )}
                </td>
              </tr>
            </tbody>
          </table>

          <Table
            columns={[
              {
                title: 'Description',
                dataIndex: 'description',
                key: 'description',
                width: '30%',
                render: (description: string, record: any, index: number) => {
                  if (isEditing && isTaskInfoEditing) {
                    return (
                      <Input
                        value={description || ''}
                        placeholder="Enter Description"
                        onChange={(e) => {
                          const updated = [...quotationItems];
                          updated[index] = { ...updated[index], description: e.target.value };
                          setQuotationItems(updated);
                        }}
                      />
                    );
                  }
                  return <span>{description || '-'}</span>;
                },
              },
              {
                title: 'Unit',
                dataIndex: 'unit',
                key: 'unit',
                width: '10%',
                render: (unit: number | string, record: any, index: number) => {
                  const unitValue = typeof unit === 'number' ? unit : parseFloat(unit || '0');
                  if (isEditing && isTaskInfoEditing) {
                    return (
                      <InputNumber
                        value={Number.isNaN(unitValue) ? 0 : unitValue}
                        onChange={(value) => {
                          const updated = [...quotationItems];
                          const next = { ...record, unit: value || 0 };
                          const price = typeof next.price_per_unit === 'number'
                            ? next.price_per_unit
                            : parseFloat(next.price_per_unit || '0');
                          const discount = typeof next.discount === 'number'
                            ? next.discount
                            : parseFloat(next.discount || '0');
                          const base = (next.unit || 0) * (price || 0) - (discount || 0);
                          const rate = next.sst_rate !== undefined && next.sst_rate !== null ? Number(next.sst_rate) : 8;
                          const sst = base * (rate / 100);
                          updated[index] = { ...next, sst, amount: base + sst };
                          setQuotationItems(updated);
                        }}
                        placeholder="Unit"
                        style={{ width: '100%' }}
                        min={0}
                        precision={2}
                      />
                    );
                  }
                  return <span>{!Number.isNaN(unitValue) ? unitValue.toFixed(2) : '0.00'}</span>;
                },
              },
              {
                title: 'Price/Unit',
                dataIndex: 'price_per_unit',
                key: 'price_per_unit',
                width: '12%',
                render: (pricePerUnit: number, record: any, index: number) => {
                  const priceValue = typeof pricePerUnit === 'number' ? pricePerUnit : parseFloat(pricePerUnit || '0');
                  if (isEditing && isTaskInfoEditing) {
                    return (
                      <InputNumber
                        value={Number.isNaN(priceValue) ? 0 : priceValue}
                        onChange={(value) => {
                          const updated = [...quotationItems];
                          const next = { ...record, price_per_unit: value || 0 };
                          const unit = typeof next.unit === 'number' ? next.unit : parseFloat(next.unit || '0');
                          const discount = typeof next.discount === 'number'
                            ? next.discount
                            : parseFloat(next.discount || '0');
                          const base = (unit || 0) * (next.price_per_unit || 0) - (discount || 0);
                          const rate = next.sst_rate !== undefined && next.sst_rate !== null ? Number(next.sst_rate) : 8;
                          const sst = base * (rate / 100);
                          updated[index] = { ...next, sst, amount: base + sst };
                          setQuotationItems(updated);
                        }}
                        style={{ width: '100%' }}
                        min={0}
                        precision={2}
                      />
                    );
                  }
                  return <span>{!Number.isNaN(priceValue) ? priceValue.toFixed(2) : '0.00'}</span>;
                },
              },
              {
                title: 'SST',
                dataIndex: 'sst',
                key: 'sst',
                width: '10%',
                render: (sst: number, record: any, index: number) => {
                  const unit = typeof record.unit === 'number' ? record.unit : parseFloat(record.unit || '0');
                  const price = typeof record.price_per_unit === 'number' ? record.price_per_unit : parseFloat(record.price_per_unit || '0');
                  const discount = typeof record.discount === 'number' ? record.discount : parseFloat(record.discount || '0');
                  const base = (unit || 0) * (price || 0) - (discount || 0);
                  const rate = record.sst_rate !== undefined && record.sst_rate !== null
                    ? Number(record.sst_rate)
                    : 8;
                  const computed = base * (rate / 100);
                  const value = sst !== undefined && sst !== null ? sst : computed;

                  if (isEditing && isTaskInfoEditing) {
                    return (
                      <div>
                        <Select
                          value={rate}
                          onChange={(nextRate) => {
                            const updated = [...quotationItems];
                            const next = { ...record, sst_rate: nextRate };
                            const nextBase = (unit || 0) * (price || 0) - (discount || 0);
                            const nextSst = nextBase * (nextRate / 100);
                            updated[index] = { ...next, sst: nextSst, amount: nextBase + nextSst };
                            setQuotationItems(updated);
                          }}
                          style={{ width: '100%' }}
                          options={[
                            { value: 8, label: '8%' },
                            { value: 0, label: '0%' },
                          ]}
                        />
                        <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
                          {Number(value || 0).toFixed(2)}
                        </div>
                      </div>
                    );
                  }

                  return <span>{!Number.isNaN(value) ? Number(value).toFixed(2) : '0.00'}</span>;
                },
              },
              {
                title: 'Discount',
                dataIndex: 'discount',
                key: 'discount',
                width: '10%',
                render: (discount: number, record: any, index: number) => {
                  const discountValue = typeof discount === 'number' ? discount : parseFloat(discount || '0');
                  if (isEditing && isTaskInfoEditing) {
                    return (
                      <InputNumber
                        value={Number.isNaN(discountValue) ? 0 : discountValue}
                        onChange={(value) => {
                          const updated = [...quotationItems];
                          const next = { ...record, discount: value || 0 };
                          const unit = typeof next.unit === 'number' ? next.unit : parseFloat(next.unit || '0');
                          const price = typeof next.price_per_unit === 'number' ? next.price_per_unit : parseFloat(next.price_per_unit || '0');
                          const base = (unit || 0) * (price || 0) - (next.discount || 0);
                          const rate = next.sst_rate !== undefined && next.sst_rate !== null ? Number(next.sst_rate) : 8;
                          const sstValue = base * (rate / 100);
                          updated[index] = { ...next, sst: sstValue, amount: base + sstValue };
                          setQuotationItems(updated);
                        }}
                        style={{ width: '100%' }}
                        min={0}
                        precision={2}
                      />
                    );
                  }
                  return <span>{!Number.isNaN(discountValue) ? discountValue.toFixed(2) : '0.00'}</span>;
                },
              },
              {
                title: 'Amount',
                dataIndex: 'amount',
                key: 'amount',
                width: '10%',
                render: (amount: number, record: any) => {
                  const unit = typeof record.unit === 'number' ? record.unit : parseFloat(record.unit || '0');
                  const price = typeof record.price_per_unit === 'number' ? record.price_per_unit : parseFloat(record.price_per_unit || '0');
                  const discount = typeof record.discount === 'number' ? record.discount : parseFloat(record.discount || '0');
                  const base = (unit || 0) * (price || 0) - (discount || 0);
                  const rate = record.sst_rate !== undefined && record.sst_rate !== null ? Number(record.sst_rate) : 8;
                  const sstValue = record.sst !== undefined && record.sst !== null ? record.sst : base * (rate / 100);
                  const computed = base + sstValue;
                  const value = amount !== undefined && amount !== null ? amount : computed;
                  return <span>{!Number.isNaN(value) ? Number(value).toFixed(2) : '0.00'}</span>;
                },
              },
              ...(isEditing && isTaskInfoEditing ? [{
                title: 'Action',
                key: 'action',
                width: '3%',
                render: (_: any, record: any, index: number) => (
                  <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setQuotationItems(quotationItems.filter((_, i) => i !== index));
                    }}
                  />
                ),
              }] : []),
            ]}
            dataSource={quotationItems.map((item, index) => ({ ...item, key: index }))}
            pagination={false}
            size="small"
            summary={() => {
              const totalAmount = quotationItems.reduce((sum, item) => {
                const value = typeof item.amount === 'number' ? item.amount : parseFloat(item.amount || '0');
                return sum + (Number.isNaN(value) ? 0 : value);
              }, 0);
              const hasActionColumn = isEditing && isTaskInfoEditing;
              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={5} />
                  <Table.Summary.Cell index={5} align="right">
                    <Text strong>{totalAmount.toFixed(2)}</Text>
                  </Table.Summary.Cell>
                  {hasActionColumn && <Table.Summary.Cell index={6} />}
                </Table.Summary.Row>
              );
            }}
          />
          {isEditing && isTaskInfoEditing && (
            <div style={{ marginTop: 12 }}>
              <Button
                type="link"
                style={{ color: '#ff4d4f', padding: 0 }}
                icon={<PlusOutlined />}
                onClick={() => {
                  setQuotationItems([...quotationItems, {
                    description: '',
                    unit: 0,
                    price_per_unit: 0,
                    discount: 0,
                    sst_rate: 8,
                    sst: 0,
                    amount: 0,
                  }]);
                }}
              >
                Add Item
              </Button>
            </div>
          )}
        </Card>
        )}


        <div style={{ 
          padding: '16px 24px', 
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 24,
          position: 'sticky',
          bottom: 0,
          backgroundColor: '#fff',
          zIndex: 10,
          
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) => {
              const currentStatus = getFieldValue('status');
              const isSent = getFieldValue('is_sent') || false;
              const normalizedStatus = normalizeStatus(currentStatus);
              // Disable Sent checkbox if status is Approve, or if not editing
              const isSentDisabled =
                (!isEditing && isEdit) || normalizedStatus === 'Approve';
              const showResend = isEditing && isEdit && normalizedStatus === 'Sent';
              
              if (isEdit && !isEditing) {
                return null;
              }

              return (
                <>
                  {canApprove && (
                    <Form.Item name="is_sent" valuePropName="checked" noStyle>
                      <Checkbox 
                        onChange={(e) => {
                          if (!isEditing) return; // Don't allow changes in view mode
                          const checked = e.target.checked;
                          // Form.Item will automatically update is_sent
                          // We just need to update status
                          if (checked) {
                            form.setFieldsValue({ status: 'Sent' });
                            setCurrentStatus('Sent');
                          } else {
                            form.setFieldsValue({ status: 'Plan', is_sent: false });
                            setCurrentStatus('Plan');
                          }
                        }}
                        disabled={isSentDisabled}
                      >
                        Sent
                      </Checkbox>
                    </Form.Item>
                  )}
                  {normalizeStatus(currentStatus) === 'Approve' && canApprove && (
                    <Checkbox
                      checked
                      disabled={!isEditing}
                      onChange={(e) => {
                        if (!isEditing) return;
                        if (!e.target.checked) {
                          form.setFieldsValue({ status: 'Plan', is_sent: false });
                          setCurrentStatus('Plan');
                        }
                      }}
                    >
                      Approve
                    </Checkbox>
                  )}
                  {showResend && canApprove && (
                    <Button type="primary" onClick={handleResend} loading={resending} disabled={!canUpdate}>
                      Resend
                    </Button>
                  )}
                </>
              );
            }}
          </Form.Item>
        </div>
        <Space>
          {isEditing && (
            <>
              <Button onClick={() => {
                if (isEdit && id) {
                  setIsEditing(false);
                  // Reload quotation data to reset form
                  loadQuotation(parseInt(id, 10));
                } else {
                  // For create mode, navigate back
                  navigate('/quotations');
                }
              }}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                onClick={() => {
                  form.submit();
                }} 
                loading={submitting}
              >
                {isEdit ? 'Save' : 'Create'}
              </Button>
            </>
          )}
          {!isEditing && isEdit && (
            <Button 
              onClick={() => navigate('/quotations')}
              disabled={false}
              style={{ pointerEvents: 'auto' }}
            >
              Cancel
            </Button>
          )}
        </Space>
      </div>
      </Form>
    </div>
  );
};
